import type { TelemetryTypes } from "@app-types/enums";
import { AsyncButton } from "@components/AsyncButton";
import { CodeSnippet } from "@components/CodeSnippet";
import { Dialog } from "@components/Dialog";
import { PageContainer } from "@components/layout/PageContainer";
import { RichTooltip } from "@components/RichTooltip";
import { SECRET_VALUE_MASK } from "@copy/global";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import FileDownloadRounded from "@mui/icons-material/FileDownloadRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useOctantStore } from "@store/octantStore";
import { useSettingsStore } from "@store/settingsStore";
import { fromMLTTypes } from "@utils/fromMltTypes";
import { getSubmittedCollectorValue, isMaskedValue } from "@utils/maskedValues";
import { toMLTTypes } from "@utils/toMltTypes";
import { useFetchManifestsAndDownload } from "@utils/useFetchManifestsAndDownload";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/shallow";
import { DeployCollectorForm } from "../../components/DeployCollectorForm";
import { SettingsCopy as copy } from "../../copy/settings/Settings.copy";
import { dDogServiceClient } from "../../services/ddog";
import { createForwardDataSnippets } from "../UpdateAgent/createForwardDataSnippets";
import "./Settings.css";

const DATADOG_SITE_PLACEHOLDER = "<datadog_site_url>";

interface AgentUpdateSnippets {
  locationUrl: string;
  code: string;
}

function telemetryTypesMatch(
  currentTelemetryTypes: TelemetryTypes[],
  savedTelemetryTypes: TelemetryTypes[],
) {
  return (
    currentTelemetryTypes.length === savedTelemetryTypes.length &&
    currentTelemetryTypes.every((type) => savedTelemetryTypes.includes(type))
  );
}

export function Settings() {
  const [telemetryTypes, setTelemetryTypes] = useState<TelemetryTypes[]>([]);
  const [savedTelemetryTypes, setSavedTelemetryTypes] = useState<
    TelemetryTypes[]
  >([]);
  const [siteHost, setSiteHost] = useState("");
  const [savedSiteHost, setSavedSiteHost] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [agentUpdateSnippets, setAgentUpdateSnippets] =
    useState<AgentUpdateSnippets | null>(null);

  const {
    connectionName,
    connectionTelemetryTypes,
    namespace,
    setInConnection,
  } = useOctantStore(
    useShallow(({ connection, setInConnection }) => ({
      connectionName: connection?.scope?.connectionName,
      connectionTelemetryTypes: connection?.telemetryTypes,
      namespace: connection?.scope?.namespace,
      setInConnection,
    })),
  );
  const { settingsStatus, showSettingsError, updateSettings } =
    useSettingsStore(
      useShallow(({ showError, status, updateSettings }) => ({
        settingsStatus: status,
        showSettingsError: showError,
        updateSettings,
      })),
    );
  const { loading: manifestsLoading, fetchAndDownload } =
    useFetchManifestsAndDownload({
      connectionName,
      namespace,
      telemetryTypes,
      mdaiVersion: "0.10.0",
    });

  const handleDownloadManifestsClick = () => {
    void fetchAndDownload();
  };

  const hasSettingsChanges =
    !telemetryTypesMatch(telemetryTypes, savedTelemetryTypes) ||
    siteHost.trim() !== savedSiteHost.trim() ||
    (!!apiKey.trim() && !isMaskedValue(apiKey));
  const loading = settingsStatus === "loading";

  useEffect(() => {
    if (!connectionName) return;

    let ignore = false;
    const activeConnectionName = connectionName;

    async function loadSettingsDefaults() {
      const [datadogIntegrations, datadogIntegration] = await Promise.all([
        dDogServiceClient.getDatadogIntegrations({}).catch(() => null),
        dDogServiceClient
          .getDatadogIntegrationByName({ name: activeConnectionName })
          .catch(() => null),
      ]);

      if (ignore) return;

      if (connectionTelemetryTypes) {
        const nextTelemetryTypes = fromMLTTypes(connectionTelemetryTypes);
        setTelemetryTypes(nextTelemetryTypes);
        setSavedTelemetryTypes(nextTelemetryTypes);
      }

      const hasDatadogIntegration =
        !!datadogIntegrations?.names.includes(activeConnectionName);

      if (hasDatadogIntegration && datadogIntegration?.url) {
        setSiteHost(datadogIntegration.url);
        setSavedSiteHost(datadogIntegration.url);
      }

      if (hasDatadogIntegration) {
        setApiKey(SECRET_VALUE_MASK);
      }
    }

    void loadSettingsDefaults();

    return () => {
      ignore = true;
    };
  }, [connectionName, connectionTelemetryTypes]);

  const handleUpdateSettings = async () => {
    if (!connectionName || !namespace) {
      showSettingsError(copy.updateSettings.missingConnectionError);
      return false;
    }

    const telemetryTypesChanged = !telemetryTypesMatch(
      telemetryTypes,
      savedTelemetryTypes,
    );
    const submittedDatadogUrl =
      siteHost.trim() === savedSiteHost.trim()
        ? ""
        : getSubmittedCollectorValue(siteHost);
    const submittedDatadogApiKey = getSubmittedCollectorValue(apiKey);

    if (telemetryTypesChanged) {
      setAgentUpdateSnippets(
        createForwardDataSnippets({
          connectionName,
          namespace,
          telemetryTypes,
          siteHost: submittedDatadogUrl || siteHost || DATADOG_SITE_PLACEHOLDER,
        }),
      );
    }

    const updated = await updateSettings({
      connectionName,
      namespace,
      telemetryTypes,
      datadogUrl: submittedDatadogUrl,
      datadogApiKey: submittedDatadogApiKey,
    });

    if (!updated) {
      return false;
    }

    setSavedTelemetryTypes(telemetryTypes);
    setInConnection("telemetryTypes", toMLTTypes(telemetryTypes));
    setSavedSiteHost(siteHost);
    setApiKey(SECRET_VALUE_MASK);

    return true;
  };

  return (
    <PageContainer
      headerActions={
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Button
            variant="secondary"
            size="small"
            disableRipple
            onClick={handleDownloadManifestsClick}
            loading={manifestsLoading}
            startIcon={<FileDownloadRounded />}
          >
            {copy.headerActions.downloadManifests}
          </Button>
          <RichTooltip
            title={copy.headerActions.gitOpsTooltip.title}
            description={copy.headerActions.gitOpsTooltip.description}
            actions={
              <Button
                className="mdai-table-toolbar-tooltip-cta-button"
                variant="text"
                endIcon={<ArrowOutwardRoundedIcon />}
                component="a"
                href={copy.headerActions.gitOpsTooltip.href}
                target="_blank"
                rel="noreferrer"
              >
                {copy.headerActions.gitOpsTooltip.cta}
              </Button>
            }
          >
            <InfoOutlined color="secondary" />
          </RichTooltip>
        </Stack>
      }
    >
      <Stack direction={"row"} spacing={3} className="settings-form-container">
        <DeployCollectorForm
          telemetryTypes={telemetryTypes}
          siteHost={siteHost}
          apiKey={apiKey}
          connectionName={connectionName}
          onTelemetryTypesChange={setTelemetryTypes}
          onSiteHostChange={setSiteHost}
          onApiKeyChange={setApiKey}
          apiKeyRequired={false}
          siteHostRequired={false}
          disabled={loading}
          submitEnabled={hasSettingsChanges}
          renderSubmitAction={({ canSubmit, validate }) => (
            <AsyncButton
              asyncFunction={() => {
                if (!validate()) return Promise.resolve(false);
                return handleUpdateSettings();
              }}
              canAsync={canSubmit}
              text={copy.updateSettings.initial}
              loadingText={copy.updateSettings.activated}
            />
          )}
        />
      </Stack>
      <Dialog
        open={!!agentUpdateSnippets}
        onClose={() => setAgentUpdateSnippets(null)}
        closeOnBackdropClick={false}
        title={copy.updateAgentDialog.title}
        actions={
          <Button
            variant="contained"
            size="small"
            onClick={() => setAgentUpdateSnippets(null)}
          >
            {copy.updateAgentDialog.cta}
          </Button>
        }
      >
        <Stack className="settings-agent-update-dialog-content" gap={2}>
          <Typography variant="body2" color="secondary">
            {copy.updateAgentDialog.description}
          </Typography>
          <Typography variant="body2" color="secondary">
            {copy.updateAgentDialog.instructions}
          </Typography>
          {agentUpdateSnippets && (
            <Stack gap={1}>
              <CodeSnippet code={agentUpdateSnippets.code} maxHeight="320px" />
            </Stack>
          )}
        </Stack>
      </Dialog>
    </PageContainer>
  );
}
